import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { StatusResult } from '@cash-web/protos/squareup/franklin/status_result.pb'
import logger from '@cash-web/shared/util-logger'
import { hasScopedSession, useAuth, useProfileCountry } from '@cash-web/shared-auth-data-access-context'
import { LoadingPage, useLogout } from '@cash-web/shared-auth-feature-main'
import { OnboardingContext, useQueryGetOnboardingWithContext } from '@cash-web/shared-blocker-data-access-api'
import { regionConfig } from '@cash-web/shared-data-access-region-provider'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { ModalPortal } from '@cash-web/shared-feature-modal'
import { FlowBlockerDescriptor } from '@cash-web/shared-flow-data-access-context'
import { WebInteractClick, WebViewViewComponent } from '@squareup/customer-data-events-web'

import { accountLockErrorBlocker } from './accountLockErrorBlocker'
import { FlowProvider, FlowRenderer, useFlow } from './FlowProvider'
import { OnboardingValidationCloseContent } from './OnboardingValidationCloseContent'

type Props = {
  children: React.ReactElement
  loadingElement?: React.ReactElement | null
}

const useShouldLockAccount = () => {
  const { profile } = useAuth()
  const profileCountry = useProfileCountry()
  const { incompleteProfileLocksAccount } = regionConfig[(profileCountry || Country.US).toString().toUpperCase()]
  return incompleteProfileLocksAccount && profile?.cashtag == null
}
const isResponseEmpty = (response?: {
  response_context?: { scenario_plan?: { blocker_descriptors?: FlowBlockerDescriptor[] }; status_result?: StatusResult }
}) => {
  return (
    (response?.response_context?.scenario_plan?.blocker_descriptors == null ||
      response?.response_context?.scenario_plan?.blocker_descriptors.length === 0) &&
    response?.response_context?.status_result == null
  )
}

export const OnboardingValidator = ({ children, loadingElement = <LoadingPage /> }: Props) => {
  const scopedSession = hasScopedSession()
  const [hasValidated, setHasValidated] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const { profile, isLoading: isLoadingAuth } = useAuth()
  const logout = useLogout()
  const logCdfEvent = useLogCashWebCdfEvent()
  const { t } = useTranslation()
  const shouldLockAccount = useShouldLockAccount()

  const accountLockedFlow = {
    trackingFlowName: 'Onboarding Validator Account Locked',
    blockerDescriptors: [
      {
        blocker: {
          form: accountLockErrorBlocker(t),
        },
      },
    ],
    queriesToInvalidate: [],
  }

  const flow = useFlow({
    initialFlow: shouldLockAccount ? accountLockedFlow : undefined,
    onCompleteFlow: () => {
      logCdfEvent(WebViewViewComponent({ componentName: 'OnboardingValidator', componentType: 'complete' }))
      setHasValidated(true)
    },
  })

  const { data, isLoading } = useQueryGetOnboardingWithContext({
    variables: {
      context: OnboardingContext.WEB_ENFORCE_ONBOARDING,
    },
    queryOptions: {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !scopedSession && window.csrfToken != null && !isLoadingAuth && profile != null,
    },
  })
  const hasData = !isResponseEmpty(data)
  const shouldStartOnboardingValidation = !hasValidated && !flow.isLoading && !flow.isInProgress && hasData
  useEffect(() => {
    if (data != null && shouldStartOnboardingValidation) {
      logger.addBreadcrumb('OnboardingValidator')
      logCdfEvent(WebViewViewComponent({ componentName: 'OnboardingValidator', componentType: 'View' }))

      flow.startWithResponse({
        trackingFlowName: 'Onboarding Validator',
        response: Promise.resolve(data),
        queriesToInvalidate: [useQueryGetOnboardingWithContext.rootQueryKey],
      })
    }
  }, [shouldStartOnboardingValidation, flow, data, logCdfEvent])
  // We are not logged in yet or we don't have a valid session so we should display the view.
  if ((!isLoadingAuth && profile == null) || window.csrfToken == null) {
    return <div>{children}</div>
  }

  // if we are loading or if we haven't validated, but we aren't in progress either, ie about to start.
  if (isLoading || flow.isLoading || (!hasValidated && !flow.isInProgress && hasData)) {
    return loadingElement
  }

  if (flow.isInProgress) {
    return (
      <FlowProvider flow={flow}>
        <ModalPortal
          onClose={() => {
            logCdfEvent(WebInteractClick({ componentName: 'OnboardingValidator', componentType: 'close modal' }))
            setShowLogoutModal(true)
          }}
          closable={!showLogoutModal}
        >
          {showLogoutModal ? (
            <OnboardingValidationCloseContent
              onClickGoBack={() => {
                logCdfEvent(
                  WebInteractClick({ componentName: 'OnboardingValidator', componentType: 'close logout modal' })
                )
                setShowLogoutModal(false)
              }}
              onClickLogOut={() => {
                logCdfEvent(WebInteractClick({ componentName: 'OnboardingValidator', componentType: 'logout' }))
                logout()
              }}
            />
          ) : (
            <FlowRenderer />
          )}
        </ModalPortal>
      </FlowProvider>
    )
  }

  // we can proceed normally
  return <div>{children}</div>
}
